import type { WorkflowPhase } from '@atlas/types'
import { Button, Checkbox, IconButton, Input, ListBoxItem, Select } from '@atlas/ui-primitives'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { type KeyboardEvent, useEffect, useState } from 'react'
import { Eyebrow } from '@/components/editorial/PageHeader'
import { PHASE_LABEL } from '@/domain/workflow-labels'

/** Enter commits immediately, same as tabbing/clicking away (both blur the field). */
function commitOnEnter(e: KeyboardEvent<HTMLInputElement>) {
	if (e.key === 'Enter') e.currentTarget.blur()
}

export interface StatusInspectorValue {
	code: string
	label: string
	phase: WorkflowPhase
	isInitial: boolean
	isTerminal: boolean
}

/** Where the status sits in its workflow's sequence, zero-based, and how many active statuses that sequence holds. */
export interface StatusSequence {
	index: number
	total: number
}

interface WorkflowStatusInspectorProps {
	value: StatusInspectorValue
	/** `onError` (optional) is called if the underlying mutation is rejected, so the caller can roll back its own optimistic local state. */
	onChange: (patch: Partial<StatusInspectorValue>, onError?: () => void) => void
	sequence: StatusSequence
	/** Put the status at this zero-based place in the sequence. */
	onMoveTo: (index: number) => void
	isMoving?: boolean
	onDelete: () => void
	isDeleting?: boolean
}

/**
 * The status's place in the work order's step list, as a step number you can
 * step through or type. This is `position`, which numbers the steps on the
 * work order detail page, orders the board's columns and decides which
 * transitions the engine classifies as backward - none of which canvas
 * coordinates touch, so a move here is a real reorder and a drag on the canvas
 * is not.
 *
 * The arrows read as a stepper on the number beside them, not as rows in a
 * list: up raises the step number (later in the chain), down lowers it. The
 * WorkflowAdmin status table keeps the opposite sense, because there the
 * arrows move a row within a vertical list and up must mean up the table.
 *
 * A status outside the sequence shows the reason in place of the controls
 * rather than a disabled field: its step number would read as zero, which is
 * not a step it could be returned to.
 */
function SequenceControl({
	sequence,
	onMoveTo,
	isMoving,
}: Pick<WorkflowStatusInspectorProps, 'sequence' | 'onMoveTo' | 'isMoving'>) {
	// The sequence is defined over active statuses only, so a deactivated status
	// holds no place in it and offers no move.
	const inSequence = sequence.index >= 0
	const [step, setStep] = useState(String(sequence.index + 1))

	// Unlike code and label, a committed step arrives back without remounting
	// this component, so the field follows the server's own answer - which is
	// not always the number typed, since the reorder renumbers every sibling.
	useEffect(() => setStep(String(sequence.index + 1)), [sequence.index])

	// A step outside the sequence is not a value the server would take, so the
	// field returns to the persisted one rather than leaving a refused number
	// on screen as if it had been accepted.
	const commitStep = () => {
		const parsed = Number(step)
		if (!Number.isInteger(parsed) || parsed < 1 || parsed > sequence.total) {
			setStep(String(sequence.index + 1))
			return
		}
		if (parsed - 1 !== sequence.index) onMoveTo(parsed - 1)
	}

	return (
		<div className="mb-3">
			<label
				htmlFor="status-step-number"
				className="mb-1 block font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle"
			>
				Step
			</label>
			{inSequence ? (
				<div className="flex items-center gap-2">
					<Input
						id="status-step-number"
						aria-label="Step number"
						type="number"
						min={1}
						max={sequence.total}
						value={step}
						disabled={isMoving}
						onChange={(e) => setStep(e.target.value)}
						onBlur={commitStep}
						onKeyDown={commitOnEnter}
						className="w-14 text-center font-mono"
					/>
					<span className="font-mono text-ds-4xs text-ds-text-subtle">of {sequence.total}</span>
					<div className="ml-1 flex gap-1.5">
						<IconButton
							variant="muted"
							size="sm"
							aria-label="Move status later"
							isDisabled={isMoving || sequence.index === sequence.total - 1}
							onPress={() => onMoveTo(sequence.index + 1)}
						>
							<ArrowUp size={14} />
						</IconButton>
						<IconButton
							variant="muted"
							size="sm"
							aria-label="Move status earlier"
							isDisabled={isMoving || sequence.index === 0}
							onPress={() => onMoveTo(sequence.index - 1)}
						>
							<ArrowDown size={14} />
						</IconButton>
					</div>
				</div>
			) : (
				<p className="font-mono text-ds-4xs text-ds-text-subtle">Inactive · outside the sequence</p>
			)}
			<p className="mt-1.5 text-ds-2xs leading-relaxed text-ds-text-muted">
				Sets the step order on the work order. Dragging on the canvas only moves the box.
			</p>
		</div>
	)
}

/**
 * The builder's per-status inspector — edits `workflow_status`'s own fields
 * directly against the admin API. Every field here is server state with no
 * client-side echo: committing straight from `value` means the control shows
 * the pre-edit value until the mutation round-trips and the query refetches,
 * which reads as "nothing happened" for a keystroke and as lag for a
 * checkbox click (worse for Initial, whose promotion path is a two-write
 * transaction). Code/Label hold a local draft committed on blur/Enter;
 * Initial/Terminal hold a local draft committed instantly on click. The
 * parent remounts this component (via `key`) when a different status is
 * selected, so every draft starts fresh for whichever status is open.
 */
export function WorkflowStatusInspector({
	value,
	onChange,
	sequence,
	onMoveTo,
	isMoving,
	onDelete,
	isDeleting,
}: WorkflowStatusInspectorProps) {
	const [code, setCode] = useState(value.code)
	const [label, setLabel] = useState(value.label)
	const [isInitial, setIsInitial] = useState(value.isInitial)
	const [isTerminal, setIsTerminal] = useState(value.isTerminal)

	return (
		<div>
			<Eyebrow className="mb-1">Status</Eyebrow>
			<div className="mb-4 font-ds-display text-xl">{label || 'Untitled status'}</div>

			<label className="mb-1 block font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">Code</label>
			<Input
				aria-label="Code"
				value={code}
				onChange={(e) => setCode(e.target.value)}
				onBlur={() => code !== value.code && onChange({ code })}
				onKeyDown={commitOnEnter}
				placeholder="machine_slug"
				className="mb-3 font-mono"
			/>

			<label className="mb-1 block font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">Label</label>
			<Input
				aria-label="Label"
				value={label}
				onChange={(e) => setLabel(e.target.value)}
				onBlur={() => label !== value.label && onChange({ label })}
				onKeyDown={commitOnEnter}
				className="mb-3"
			/>

			<SequenceControl sequence={sequence} onMoveTo={onMoveTo} isMoving={isMoving} />

			<label className="mb-1 block font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">Phase</label>
			<Select
				aria-label="Phase"
				selectedKey={value.phase}
				onSelectionChange={(key) => onChange({ phase: key as WorkflowPhase })}
				className="mb-3"
			>
				{Object.entries(PHASE_LABEL).map(([phase, label]) => (
					<ListBoxItem key={phase} id={phase}>
						{label}
					</ListBoxItem>
				))}
			</Select>

			<div className="flex flex-col gap-2 border-t border-ds-border py-3">
				<Checkbox
					size="sm"
					isSelected={isInitial}
					// The API always rejects unchecking the current initial status
					// directly (promote a different status instead), and rejects setting
					// Initial while Terminal is already set (a status can't be both) —
					// disabling both cases up front avoids firing a mutation that's
					// guaranteed to fail.
					isDisabled={isInitial || isTerminal}
					onChange={(v) => {
						if (!v) return
						setIsInitial(v)
						onChange({ isInitial: v }, () => setIsInitial(!v))
					}}
				>
					Initial status — where new orders land
					{isInitial
						? ' (promote another status to change)'
						: isTerminal
							? ' (a terminal status cannot be initial)'
							: ''}
				</Checkbox>
				<Checkbox
					size="sm"
					isSelected={isTerminal}
					isDisabled={isInitial}
					onChange={(v) => {
						setIsTerminal(v)
						onChange({ isTerminal: v }, () => setIsTerminal(!v))
					}}
				>
					Terminal status — no outgoing work{isInitial ? ' (an initial status cannot be terminal)' : ''}
				</Checkbox>
			</div>

			<Button
				variant="muted"
				className="mt-2 w-full rounded-ds-full border-ds-danger text-ds-danger"
				onPress={onDelete}
				isDisabled={isDeleting}
			>
				{isDeleting ? 'Deleting…' : 'Delete status'}
			</Button>
		</div>
	)
}
